import { useTranslation } from 'react-i18next';
import { InfiosChip, InfiosContainer } from '@infios/uikit/react';
import type { HealthClassification, OrderInsightPayload, OrderLine } from '../../interfaces/chat';
import { FormattedDateTime } from '../FormattedDateTime';
import styles from './OrderHeaderCard.module.css';

interface OrderHeaderCardProps {
  orderId: string;
  health: OrderInsightPayload['health'];
  order: OrderInsightPayload['order'];
}

/**
 * Fixed classification → color mapping (design doc §3), applied via InfiosChip's
 * backgroundColor/textColor override props (Chip has no built-in "success"/
 * "warning" variant). Lives in the UI as a static lookup so the visual language
 * stays under design control — the backend only ever sends the enum, never a color.
 */
const HEALTH_PILL_COLOR: Record<HealthClassification, { backgroundColor: string; textColor: string }> = {
  ON_TRACK: { backgroundColor: 'var(--chat-success-subtle)', textColor: 'var(--chat-success)' },
  NEEDS_ATTENTION: { backgroundColor: 'var(--chat-warning-subtle)', textColor: 'var(--chat-warning)' },
  AT_RISK: { backgroundColor: 'var(--chat-error-subtle)', textColor: 'var(--chat-error)' },
  SLA_BREACHED: { backgroundColor: '#f6d9d7', textColor: '#7a1712' },
  DELIVERED: { backgroundColor: 'var(--chat-info-subtle)', textColor: 'var(--chat-info)' },
};

const LINE_SEVERITY_COLOR: Record<OrderLine['statusSeverity'], { backgroundColor: string; textColor: string }> = {
  NEUTRAL: { backgroundColor: 'var(--chat-bg-alt)', textColor: 'var(--chat-text-secondary)' },
  WARNING: { backgroundColor: 'var(--chat-warning-subtle)', textColor: 'var(--chat-warning)' },
  CRITICAL: { backgroundColor: 'var(--chat-error-subtle)', textColor: 'var(--chat-error)' },
};

export function OrderHeaderCard({ orderId, health, order }: OrderHeaderCardProps) {
  const { t } = useTranslation();

  // Invariant #1: a health pill never renders without health.summary alongside it.
  const showHealthPill = Boolean(health.summary);

  return (
    <InfiosContainer type="card" className={styles.card} data-testid="order-header-card">
      <div className={styles.headRow}>
        <span className={styles.orderId}>{orderId}</span>
        {showHealthPill ? (
          <InfiosChip
            size="compact"
            data-health-status={health.classification}
            {...HEALTH_PILL_COLOR[health.classification]}
          >
            {t(`ChatbotUI.OrderInsight.Health_${health.classification}`)}
          </InfiosChip>
        ) : null}
      </div>
      {showHealthPill ? <p className={styles.healthSummary}>{health.summary}</p> : null}

      <dl className={styles.grid}>
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.Customer')}</dt>
          <dd>{order.customerName || '—'}</dd>
        </div>
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.Company')}</dt>
          <dd>{order.companyName || '—'}</dd>
        </div>
        {order.accountTier ? (
          <div className={styles.field}>
            <dt>{t('ChatbotUI.OrderInsight.AccountTier')}</dt>
            <dd>{order.accountTier}</dd>
          </div>
        ) : null}
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.OrderDate')}</dt>
          <dd>
            <FormattedDateTime iso={order.orderDate} />
          </dd>
        </div>
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.SlaDate')}</dt>
          <dd>
            <FormattedDateTime iso={order.slaDate} />
          </dd>
        </div>
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.Channel')}</dt>
          <dd>{order.channel || '—'}</dd>
        </div>
        <div className={styles.field}>
          <dt>{t('ChatbotUI.OrderInsight.PaymentStatus')}</dt>
          <dd>{order.paymentStatus || '—'}</dd>
        </div>
      </dl>

      {order.lines.length > 0 ? (
        <table className={styles.lines}>
          <caption className={styles.linesCaption}>{t('ChatbotUI.OrderInsight.LineItems')}</caption>
          <thead>
            <tr>
              <th>{t('ChatbotUI.OrderInsight.Sku')}</th>
              <th>{t('ChatbotUI.OrderInsight.Description')}</th>
              <th>{t('ChatbotUI.OrderInsight.Qty')}</th>
              <th>{t('ChatbotUI.OrderInsight.Dc')}</th>
              <th>{t('ChatbotUI.OrderInsight.Status')}</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((line, idx) => (
              <tr key={`${line.sku}-${idx}`}>
                <td className={styles.sku}>{line.sku}</td>
                <td>{line.description}</td>
                <td>{line.qty}</td>
                <td>{line.dcAssignment ?? '—'}</td>
                <td>
                  <InfiosChip size="compact" {...LINE_SEVERITY_COLOR[line.statusSeverity]}>
                    {line.status}
                  </InfiosChip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </InfiosContainer>
  );
}
